/**
 * deliveryApi — cliente HTTP do módulo Empreende Delivery.
 *
 * Mantido isolado para refletir a arquitetura backend (módulo `/api/delivery/*`).
 * Quando o módulo virar microserviço, basta trocar `api` por um axios próprio.
 */
import { api } from "./api";

export const deliveryApi = {
  // ===== Lojas =====
  createLoja: (data) => api.post("/delivery/lojas", data).then((r) => r.data),
  getLojaByNegocio: (negocioId) =>
    api.get(`/delivery/lojas/by-negocio/${negocioId}`).then((r) => r.data),
  getLoja: (lojaId) => api.get(`/delivery/lojas/${lojaId}`).then((r) => r.data),
  updateLoja: (lojaId, data) =>
    api.put(`/delivery/lojas/${lojaId}`, data).then((r) => r.data),
  toggleLojaStatus: (lojaId, ativo) =>
    api.patch(`/delivery/lojas/${lojaId}/status`, null, { params: { ativo } }).then((r) => r.data),

  // ===== Cardápio (público) =====
  getCardapio: (lojaId) =>
    api.get(`/delivery/lojas/${lojaId}/cardapio`).then((r) => r.data),
  getPublicCardapio: (municipioSlug, negocioSlug) =>
    api
      .get(`/delivery/public/cardapio/${municipioSlug}/${negocioSlug}`)
      .then((r) => r.data),

  // ===== Produtos =====
  createProduto: (data) =>
    api.post("/delivery/produtos", data).then((r) => r.data),
  updateProduto: (produtoId, data) =>
    api.put(`/delivery/produtos/${produtoId}`, data).then((r) => r.data),
  toggleProdutoDisponibilidade: (produtoId, disponivel) =>
    api
      .patch(`/delivery/produtos/${produtoId}/disponibilidade`, null, {
        params: { disponivel },
      })
      .then((r) => r.data),
  deleteProduto: (produtoId) =>
    api.delete(`/delivery/produtos/${produtoId}`).then((r) => r.data),
  uploadProdutoFoto: (produtoId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post(`/delivery/produtos/${produtoId}/foto`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  deleteProdutoFoto: (produtoId) =>
    api.delete(`/delivery/produtos/${produtoId}/foto`).then((r) => r.data),

  // ===== Zonas =====
  listZonas: (lojaId) =>
    api.get(`/delivery/lojas/${lojaId}/zonas`).then((r) => r.data),
  createZona: (data) =>
    api.post("/delivery/zonas", data).then((r) => r.data),
  updateZona: (zonaId, data) =>
    api.put(`/delivery/zonas/${zonaId}`, data).then((r) => r.data),
  deleteZona: (zonaId) =>
    api.delete(`/delivery/zonas/${zonaId}`).then((r) => r.data),

  // ===== Pedidos =====
  createPedido: (data) =>
    api.post("/delivery/pedidos", data).then((r) => r.data),
  listPedidos: (params) =>
    api.get("/delivery/pedidos", { params }).then((r) => r.data),
  getPedido: (pedidoId) =>
    api.get(`/delivery/pedidos/${pedidoId}`).then((r) => r.data),
  updateStatus: (pedidoId, status, observacao) =>
    api
      .patch(`/delivery/pedidos/${pedidoId}/status`, { status, observacao })
      .then((r) => r.data),
  cancelarPedido: (pedidoId, motivo) =>
    api
      .patch(`/delivery/pedidos/${pedidoId}/cancelar`, null, {
        params: motivo ? { motivo } : {},
      })
      .then((r) => r.data),
  trackPedido: (numeroCurto) =>
    api
      .get(`/delivery/public/pedido/${encodeURIComponent(String(numeroCurto).replace(/^#/, ""))}`)
      .then((r) => r.data),

  // ===== Notificações =====
  listNotificacoes: (params) =>
    api.get("/delivery/notificacoes", { params }).then((r) => r.data),
  markNotifLida: (notifId) =>
    api.patch(`/delivery/notificacoes/${notifId}/lida`).then((r) => r.data),

  // ===== Admin =====
  adminActivate: (data) =>
    api.post("/delivery/admin/activate", data).then((r) => r.data),

  // ===== Categorias do cardápio =====
  addCategoria: (cardapioId, categoria) =>
    api.post(`/delivery/cardapios/${cardapioId}/categorias`, categoria).then((r) => r.data),
  removeCategoria: (cardapioId, categoriaId) =>
    api.delete(`/delivery/cardapios/${cardapioId}/categorias/${categoriaId}`).then((r) => r.data),

  // ===== Cupons =====
  createCupom: (data) => api.post("/delivery/cupons", data).then((r) => r.data),
  listCupons: (lojaId) =>
    api.get(`/delivery/lojas/${lojaId}/cupons`).then((r) => r.data),
  updateCupom: (cupomId, data) =>
    api.put(`/delivery/cupons/${cupomId}`, data).then((r) => r.data),
  deleteCupom: (cupomId) =>
    api.delete(`/delivery/cupons/${cupomId}`).then((r) => r.data),
  validarCupomPublico: (lojaId, codigo, subtotal) =>
    api
      .get("/delivery/public/cupom/validar", {
        params: { loja_id: lojaId, codigo, subtotal },
      })
      .then((r) => r.data),

  // ===== Dashboard =====
  getDashboard: (lojaId) =>
    api.get(`/delivery/lojas/${lojaId}/dashboard`).then((r) => r.data),

  // ===== Cupom de fidelidade 1-click =====
  fidelidadeCupom1Click: (pedidoId, params = {}) =>
    api
      .post(`/delivery/pedidos/${pedidoId}/fidelidade-cupom`, null, { params })
      .then((r) => r.data),

  // ===== Admin pedidos =====
  adminListPedidos: (params) =>
    api.get("/delivery/admin/pedidos", { params }).then((r) => r.data),
  adminPedidosCsvUrl: (params = {}) => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v))
    ).toString();
    return `${base}/api/delivery/admin/pedidos.csv${qs ? `?${qs}` : ""}`;
  },
};

export default deliveryApi;
